import { useRef, useState, type KeyboardEvent } from "react";

import { cn } from "../lib/cn";
import { controlShellBase } from "../lib/control";
import { XIcon } from "../lib/icons";
import { useFieldControl } from "./field";

export interface TagsInputProps {
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (tags: string[]) => void;
  placeholder?: string;
  /** Au-delà, la saisie est bloquée. */
  max?: number;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/**
 * Champ à jetons : mots-clés, destinataires, étiquettes.
 *
 * Conventions clavier reprises des clients de messagerie, parce que ce sont
 * celles que les gens connaissent déjà :
 *   Entrée ou virgule  → valide le jeton en cours
 *   Retour arrière     → supprime le dernier jeton si la saisie est vide
 *   Coller             → découpe sur les virgules et crée plusieurs jetons
 *
 * Le conteneur imite un champ de saisie (`controlShellBase`) et porte le focus
 * via `focus-within`, puisque l'élément réellement focalisé est le `<input>`
 * interne.
 */
export function TagsInput({
  value,
  defaultValue = [],
  onValueChange,
  placeholder = "Ajouter…",
  max,
  disabled,
  className,
  id,
}: TagsInputProps) {
  const [internal, setInternal] = useState(defaultValue);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const isControlled = value !== undefined;
  const tags = isControlled ? value : internal;
  const isFull = max !== undefined && tags.length >= max;

  const fieldProps = useFieldControl({ id });

  const commit = (next: string[]) => {
    if (!isControlled) setInternal(next);
    onValueChange?.(next);
  };

  const add = (raw: string) => {
    const cleaned = raw.trim();
    // Pas de doublon : deux jetons identiques n'apportent rien et cassent
    // l'unicité des clés de rendu.
    if (!cleaned || isFull || tags.includes(cleaned)) return;
    commit([...tags, cleaned]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      add(draft);
      setDraft("");
      return;
    }
    if (event.key === "Backspace" && draft === "" && tags.length > 0) {
      commit(tags.slice(0, -1));
    }
  };

  return (
    <div
      className={cn(
        controlShellBase,
        "flex flex-wrap items-center gap-1.5 p-1.5",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      // Cliquer n'importe où dans le conteneur focalise la saisie : la zone
      // cliquable doit correspondre à la zone perçue comme un champ.
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex animate-pop items-center gap-1 rounded-sm bg-accent-subtle py-0.5 pr-1 pl-2 text-sm text-accent-fg"
        >
          {tag}
          <button
            type="button"
            tabIndex={-1}
            onClick={(event) => {
              event.stopPropagation();
              commit(tags.filter((item) => item !== tag));
            }}
            aria-label={`Retirer ${tag}`}
            className="rounded-sm p-0.5 transition-colors duration-fast hover:bg-accent hover:text-fg-on-accent"
          >
            <XIcon className="size-3" />
          </button>
        </span>
      ))}

      <input
        ref={inputRef}
        type="text"
        value={draft}
        disabled={disabled || isFull}
        placeholder={isFull ? `Maximum ${max} atteint` : placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        // La saisie en cours est validée si l'on quitte le champ : sinon elle
        // est perdue en silence, ce qui est toujours vécu comme un bug.
        onBlur={() => {
          add(draft);
          setDraft("");
        }}
        onPaste={(event) => {
          const pasted = event.clipboardData.getData("text");
          if (!pasted.includes(",")) return;
          event.preventDefault();
          for (const part of pasted.split(",")) add(part);
        }}
        className="min-w-24 flex-1 bg-transparent px-1.5 py-0.5 text-sm text-fg placeholder:text-fg-muted focus:outline-none"
        {...fieldProps}
      />
    </div>
  );
}
